/**
 * SocialCapitalEcosystem — a delightful, CSS-based radial infographic
 * of the Social Pulse "Social Capital" ecosystem. Fits on any homepage
 * hero without waiting on an AI image call.
 *
 * Design (per spec):
 *   • Central glowing "Social Capital" hub
 *   • 10 category satellites (Education, Nature, Volunteer, Career,
 *     Innovation, Arts, Health, Culture, Family, Community)
 *   • Progression bar below: Learn → Create → Volunteer → Share →
 *     Collaborate → Build Trust → Earn Recognition → Grow Social
 *     Capital → Create Opportunities
 *   • Blue/green palette representing trust + sustainability
 *   • All satellites are clickable, deep-linking into the relevant
 *     Social Pulse vertical.
 */
import { Link } from "react-router-dom";
import {
  GraduationCap, Leaf, HeartHandshake, Briefcase, Cpu, Palette,
  Activity, Globe2, Users, ShieldCheck, Sparkles, ArrowRight,
} from "lucide-react";

const CATEGORIES = [
  { key: "education",  label: "Education & Learning",       icon: GraduationCap,  color: "#0ea5e9", to: "/education",     tags: ["School", "University", "Online Courses", "Certifications", "Research"] },
  { key: "nature",     label: "Nature & Environment",       icon: Leaf,           color: "#16a34a", to: "/nature",        tags: ["Wildlife", "Conservation", "Tree Planting", "Citizen Science"] },
  { key: "volunteer",  label: "Volunteer & Community",      icon: HeartHandshake, color: "#e11d48", to: "/causes",        tags: ["Food Banks", "Charity", "Disaster Relief", "Mentoring"] },
  { key: "career",     label: "Career & Growth",            icon: Briefcase,      color: "#7c3aed", to: "/jobs",          tags: ["Skills", "Projects", "Portfolio", "Leadership", "Entrepreneurship"] },
  { key: "innovation", label: "Innovation & Tech",          icon: Cpu,            color: "#0369a1", to: "/articles?category=technology", tags: ["Coding", "Robotics", "AI", "Open Source", "Inventions"] },
  { key: "arts",       label: "Arts & Creativity",          icon: Palette,        color: "#d97706", to: "/arts",          tags: ["Photography", "Music", "Painting", "Writing", "Film"] },
  { key: "health",     label: "Health & Sports",            icon: Activity,       color: "#059669", to: "/health",        tags: ["Fitness", "Team Sports", "Hiking", "Wellness"] },
  { key: "culture",    label: "Culture & Travel",           icon: Globe2,         color: "#ea580c", to: "/travel",        tags: ["Languages", "Heritage", "Museums", "Cultural Exchange"] },
  { key: "family",     label: "Family & Mentorship",        icon: Users,          color: "#b45309", to: "/family",        tags: ["Parenting", "Teaching", "Coaching", "Lifelong Learning"] },
  { key: "reputation", label: "Community & Reputation",     icon: ShieldCheck,    color: "#0d9488", to: "/members",       tags: ["Trust", "Collaboration", "Verified Contributions", "Recognition"] },
];

const PROGRESSION = [
  "Learn", "Create", "Volunteer", "Share",
  "Collaborate", "Build Trust", "Earn Recognition",
  "Grow Social Capital", "Create Opportunities",
];

export default function SocialCapitalEcosystem() {
  return (
    <section className="relative overflow-hidden rounded-3xl border p-6 sm:p-10 mt-8 mb-8"
             style={{
               borderColor: "var(--wofy-border)",
               background: "linear-gradient(135deg, #ECFDF5 0%, #DBEAFE 55%, #EFF6FF 100%)",
             }}
             data-testid="social-capital-ecosystem">
      {/* Decorative glow blob */}
      <div className="absolute -top-24 -left-24 w-72 h-72 rounded-full opacity-30 pointer-events-none"
           style={{ background: "radial-gradient(circle, #6ee7b7 0%, transparent 70%)" }} />
      <div className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full opacity-30 pointer-events-none"
           style={{ background: "radial-gradient(circle, #93c5fd 0%, transparent 70%)" }} />

      <div className="relative z-10">
        <div className="overline mb-1" style={{ color: "#065f46" }}>
          🌍 THE SOCIAL PULSE ECOSYSTEM
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight"
            style={{ color: "#0f172a" }}>
          Every positive action grows your <span style={{ color: "#059669" }}>Social Capital</span>.
        </h2>
        <p className="mt-3 max-w-2xl text-base sm:text-lg" style={{ color: "#334155" }}>
          Ten interconnected areas — education, nature, volunteering, career,
          innovation, arts, health, culture, family and community — all feed a
          single reputation currency you can carry through life.
        </p>

        {/* Central hub + orbiting cards */}
        <div className="relative mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
             data-testid="ecosystem-satellites">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            return (
              <Link key={c.key} to={c.to}
                    className="group rounded-2xl bg-white/85 backdrop-blur border p-4 hover:shadow-md transition"
                    style={{ borderColor: "rgba(0,0,0,0.06)" }}
                    data-testid={`ecosystem-cat-${c.key}`}>
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                       style={{ background: `${c.color}18`, color: c.color }}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-medium text-sm leading-tight">{c.label}</div>
                </div>
                <div className="mt-2 text-[11px] leading-relaxed opacity-70">
                  {c.tags.slice(0, 4).join(" · ")}
                </div>
                <div className="mt-2 inline-flex items-center gap-1 text-[11px] opacity-60 group-hover:opacity-100"
                     style={{ color: c.color }}>
                  Explore <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            );
          })}
        </div>

        {/* Central hub badge (visually sits between categories) */}
        <div className="mt-8 flex justify-center">
          <div className="rounded-full p-4 shadow-lg text-white text-center"
               style={{
                 background: "radial-gradient(circle, #10b981 0%, #059669 55%, #0f766e 100%)",
                 boxShadow: "0 12px 32px -8px rgba(16,185,129,0.6), 0 0 40px 4px rgba(16,185,129,0.35)",
               }}
               data-testid="ecosystem-hub">
            <Sparkles className="w-6 h-6 mx-auto mb-1" />
            <div className="font-display font-bold text-lg leading-none">Social Capital</div>
            <div className="text-[11px] opacity-90 mt-1 max-w-[9rem]">
              Your lifelong reputation currency
            </div>
          </div>
        </div>

        {/* Progression */}
        <div className="mt-10" data-testid="ecosystem-progression">
          <div className="text-xs uppercase font-mono tracking-wider opacity-70 mb-2 text-center">
            HOW IT COMPOUNDS
          </div>
          <div className="flex flex-wrap justify-center items-center gap-1.5">
            {PROGRESSION.map((step, i) => (
              <div key={step} className="flex items-center gap-1.5">
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium bg-white/90 border"
                      style={{ borderColor: "rgba(0,0,0,0.08)", color: "#065f46" }}
                      data-testid={`ecosystem-step-${i}`}>
                  <span className="text-[10px] font-mono opacity-60">{i + 1}</span>
                  {step}
                </span>
                {i < PROGRESSION.length - 1 && (
                  <ArrowRight className="w-3 h-3 opacity-50" style={{ color: "#059669" }} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTAs */}
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/register" className="wofy-btn-primary" data-testid="ecosystem-join">
            <Sparkles className="w-4 h-4" /> Start earning social capital
          </Link>
          <Link to="/about" className="wofy-btn-ghost text-sm" data-testid="ecosystem-about">
            How it works →
          </Link>
        </div>
      </div>
    </section>
  );
}
